/**
 * Состояние экрана результатов живёт в URL (MVP_TECH_RFC §22).
 *
 * Здесь же лежит идентификатор прогона (`?run=…`): без него ссылка на
 * результаты не воспроизводится, а после перезагрузки страницы экран не знал бы,
 * какой подбор показывать.
 *
 * Главное правило раздела: **дополнительные фильтры не могут ослабить
 * обязательные критерии** (MVP_PRD §19). Они работают поверх множества, уже
 * прошедшего hard-фильтры, поэтому «разрешить дизель», если пользователь его
 * исключил, отсюда невозможно — для этого есть только «Изменить запрос».
 *
 * Разбор устроен как в каталоге: прочитать → провалидировать → выбросить
 * непонятное. Цена в адресе в евро, в API уходит в центах.
 */

import { z } from 'zod';

import { SHOWCASE_SORTS, type ShowcaseSort } from '@entities/search-run';
import { FEATURE_CODES, type FeatureCode } from '@entities/search-session';
import type { Drivetrain, FuelType, Transmission } from '@entities/vehicle/types';

/**
 * Допустимые значения фильтров результатов.
 *
 * Раньше эти константы жили в `catalogQuery.ts` вместе с фильтрами публичного
 * каталога. Каталог удалён (решение заказчика 2026-08-27), а фильтры результатов
 * остались — константы переехали сюда, к единственному оставшемуся потребителю.
 * `satisfies` держит их в согласии с типами каталога: опечатка в значении не
 * доживёт до рантайма.
 */
const FUEL_TYPES = [
  'petrol',
  'diesel',
  'mild_hybrid',
  'full_hybrid',
  'plug_in_hybrid',
  'electric',
] as const satisfies readonly FuelType[];

const TRANSMISSIONS = ['automatic', 'manual'] as const satisfies readonly Transmission[];

const DRIVETRAINS = ['fwd', 'rwd', 'awd'] as const satisfies readonly Drivetrain[];

/**
 * Allowlist сортировок результатов (MVP_PRD §19).
 *
 * `match_desc` и `confidence_desc` доступны только здесь: без выполненного
 * подбора сортировать по соответствию нечем.
 */
export const RESULT_SORTS = [
  'match_desc',
  'price_asc',
  'price_desc',
  'registration_desc',
  'mileage_asc',
  'newest',
  'confidence_desc',
] as const;

export type ResultSort = (typeof RESULT_SORTS)[number];

const sortSchema = z.enum(RESULT_SORTS);
const showcaseSortSchema = z.enum(SHOWCASE_SORTS);
/** Флаг в адресе — только литерал `true`; «1», «yes» и прочее выбрасываются. */
const flagSchema = z.literal('true');

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;
const MAX_PRICE_EUR = 10_000_000;
const MAX_MILEAGE_KM = 2_000_000;
const MAX_SCORE = 100;
const CENTS_IN_EURO = 100;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const positiveInt = (max: number) =>
  z
    .string()
    .regex(/^\d+$/)
    .transform(Number)
    .refine((value) => value >= 0 && value <= max);

const trimmedText = z.string().trim().min(1).max(100);

export interface ResultQuery {
  /** Прогон подбора. `undefined` — берётся текущий прогон активной сессии. */
  run: string | undefined;
  page: number;
  /** `undefined` — порядок не выбран: витрина покажет свой, оплаченный список — по соответствию. */
  sort: ResultSort | undefined;
  make: string | undefined;
  model: string | undefined;
  /** Евро: адрес читают люди. В API уходит в центах. */
  priceMin: number | undefined;
  priceMax: number | undefined;
  yearMin: number | undefined;
  mileageMax: number | undefined;
  fuelTypes: FuelType[];
  transmissions: Transmission[];
  drivetrains: Drivetrain[];
  featureCodes: FeatureCode[];
  /** Только автомобили без признаков внимания. */
  withoutAttention: boolean;
  minScore: number | undefined;
  /** «Meine Favoriten»: только отмеченные автомобили набора (BE-581). */
  favoritesOnly: boolean;
}

export const EMPTY_RESULT_QUERY: ResultQuery = {
  run: undefined,
  page: 1,
  sort: undefined,
  make: undefined,
  model: undefined,
  priceMin: undefined,
  priceMax: undefined,
  yearMin: undefined,
  mileageMax: undefined,
  fuelTypes: [],
  transmissions: [],
  drivetrains: [],
  featureCodes: [],
  withoutAttention: false,
  minScore: undefined,
  favoritesOnly: false,
};

function pickKnown<T extends string>(values: string[], allowed: readonly T[]): T[] {
  return allowed.filter((option) => values.includes(option));
}

function readNumber(params: URLSearchParams, key: string, max: number): number | undefined {
  const raw = params.get(key);
  if (raw === null) return undefined;
  const parsed = positiveInt(max).safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

function readText(params: URLSearchParams, key: string): string | undefined {
  const raw = params.get(key);
  if (raw === null) return undefined;
  const parsed = trimmedText.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

/** Адрес → проверенный набор фильтров. Непонятные значения отбрасываются. */
export function parseResultQuery(params: URLSearchParams): ResultQuery {
  const sort = params.get('sort');
  const run = params.get('run');
  const priceMin = readNumber(params, 'price_min', MAX_PRICE_EUR);
  const priceMax = readNumber(params, 'price_max', MAX_PRICE_EUR);
  const yearMin = readNumber(params, 'year_min', MAX_YEAR);

  return {
    // Не UUID до backend не доезжает: чужой формат идентификатора — это
    // испорченная ссылка, а не «прогон не найден».
    run: run !== null && UUID_PATTERN.test(run) ? run : undefined,
    page: readNumber(params, 'page', Number.MAX_SAFE_INTEGER) ?? 1,
    // Непонятное значение — то же, что и отсутствующее: порядок не выбран.
    // Подставлять `match_desc` нельзя — до оплаты его отклонит backend.
    sort: sort === null ? undefined : sortSchema.safeParse(sort).data,
    make: readText(params, 'make'),
    model: readText(params, 'model'),
    // Перевёрнутый диапазон меняется местами, а не выбрасывается целиком.
    priceMin:
      priceMin !== undefined && priceMax !== undefined ? Math.min(priceMin, priceMax) : priceMin,
    priceMax:
      priceMin !== undefined && priceMax !== undefined ? Math.max(priceMin, priceMax) : priceMax,
    yearMin: yearMin !== undefined && yearMin >= MIN_YEAR ? yearMin : undefined,
    mileageMax: readNumber(params, 'mileage_max', MAX_MILEAGE_KM),
    fuelTypes: pickKnown(params.getAll('fuel_type'), FUEL_TYPES),
    transmissions: pickKnown(params.getAll('transmission'), TRANSMISSIONS),
    drivetrains: pickKnown(params.getAll('drivetrain'), DRIVETRAINS),
    featureCodes: pickKnown(params.getAll('feature'), FEATURE_CODES),
    withoutAttention: params.get('no_attention') === 'true',
    minScore: readNumber(params, 'min_score', MAX_SCORE),
    favoritesOnly: flagSchema.safeParse(params.get('favorites_only')).success,
  };
}

/**
 * Сортировка годится для витрины до оплаты.
 *
 * Список витрины — контракт API (BE-580) и живёт в сущности. Что он не выходит
 * за allowlist адреса, держит сам компилятор: тип-предикат обязан сужать тип
 * параметра, и лишнее значение в `SHOWCASE_SORTS` не скомпилируется.
 */
export function isShowcaseSort(sort: ResultSort | undefined): sort is ShowcaseSort {
  return showcaseSortSchema.safeParse(sort).success;
}

/**
 * Адрес витрины до оплаты (BE-580): то, что backend до оплаты отклоняет,
 * выбрасывается **до запроса**.
 *
 * Сортировки по соответствию и уверенности, порог Match Score, «без признаков
 * внимания» и коды оснащения работают по аналитике — до оплаты её нет, и
 * запрос с ними получил бы `400`. Битая ссылка — это не ошибка загрузки:
 * выдача открывается в порядке по умолчанию, как если бы параметра не было.
 */
export function toShowcaseQuery(query: ResultQuery): ResultQuery {
  return {
    ...query,
    sort: isShowcaseSort(query.sort) ? query.sort : undefined,
    minScore: undefined,
    withoutAttention: false,
    featureCodes: [],
  };
}

/** Фильтры → адрес. Значения по умолчанию не пишутся: адрес остаётся коротким. */
export function toResultSearchParams(query: ResultQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.run !== undefined) params.set('run', query.run);
  if (query.page > 1) params.set('page', String(query.page));
  if (query.sort !== undefined) params.set('sort', query.sort);
  if (query.make) params.set('make', query.make);
  if (query.model) params.set('model', query.model);
  if (query.priceMin !== undefined) params.set('price_min', String(query.priceMin));
  if (query.priceMax !== undefined) params.set('price_max', String(query.priceMax));
  if (query.yearMin !== undefined) params.set('year_min', String(query.yearMin));
  if (query.mileageMax !== undefined) params.set('mileage_max', String(query.mileageMax));
  if (query.withoutAttention) params.set('no_attention', 'true');
  if (query.minScore !== undefined) params.set('min_score', String(query.minScore));
  if (query.favoritesOnly) params.set('favorites_only', 'true');
  for (const value of query.fuelTypes) params.append('fuel_type', value);
  for (const value of query.transmissions) params.append('transmission', value);
  for (const value of query.drivetrains) params.append('drivetrain', value);
  for (const value of query.featureCodes) params.append('feature', value);
  return params;
}

/**
 * Сброс фильтров: прогон, сортировка и «Meine Favoriten» сохраняются, страница
 * возвращается к первой. Избранное — не фильтр выдачи, а закладка заказа, и
 * снимается своим переключателем.
 */
export function resetResultFilters(query: ResultQuery): ResultQuery {
  return {
    ...EMPTY_RESULT_QUERY,
    run: query.run,
    sort: query.sort,
    favoritesOnly: query.favoritesOnly,
  };
}

/**
 * Параметры адреса карточки автомобиля, открытой из списка (FE-586, FE-587).
 *
 * Переносятся прогон, сортировка, «Meine Favoriten» и страница: по первым трём
 * backend считает «Auto N von M», по всем четырём «Zurück zur Liste» вернёт
 * человека туда, откуда он ушёл.
 */
export function toVehicleLinkSearch(query: ResultQuery): string {
  // Та же запись, что у адреса списка: «назад» приносит строку в том же
  // порядке, и экран списка не переписывает её второй раз.
  const search = toResultSearchParams({
    ...EMPTY_RESULT_QUERY,
    run: query.run,
    sort: query.sort,
    favoritesOnly: query.favoritesOnly,
    page: query.page,
  }).toString();
  return search === '' ? '' : `?${search}`;
}

/** Фильтры → параметры API. Евро превращаются в центы здесь, а не в компоненте. */
export function toResultsApiQuery(query: ResultQuery, pageSize: number) {
  return {
    page: query.page,
    pageSize,
    sort: query.sort,
    make: query.make,
    model: query.model,
    priceMinCents: query.priceMin === undefined ? undefined : query.priceMin * CENTS_IN_EURO,
    priceMaxCents: query.priceMax === undefined ? undefined : query.priceMax * CENTS_IN_EURO,
    yearMin: query.yearMin,
    mileageMaxKm: query.mileageMax,
    fuelTypes: query.fuelTypes,
    transmissions: query.transmissions,
    drivetrains: query.drivetrains,
    featureCodes: query.featureCodes,
    withoutAttention: query.withoutAttention,
    minScore: query.minScore,
    favoritesOnly: query.favoritesOnly,
  };
}
